import CryptoJS from 'crypto-js';

const ENCRYPTION_KEY =
  process.env.REACT_APP_STORAGE_ENCRYPTION_KEY || 'default-key-change-in-production'; // MUST be set in production

/** CryptoJS default OpenSSL / salted ciphertext typically starts with this base64 prefix */
function looksLikeCryptoJsCiphertext(value) {
  return typeof value === 'string' && value.startsWith('U2FsdGVkX1');
}

/** JWT: header.payload.signature (base64url segments) */
function looksLikeJwt(value) {
  if (typeof value !== 'string' || value.length < 20) return false;
  const parts = value.split('.');
  return (
    parts.length === 3 &&
    parts.every((p) => p.length > 0 && /^[A-Za-z0-9_-]+$/.test(p))
  );
}

function parseStoredPlain(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function safeUtf8FromDecrypted(decrypted) {
  try {
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch {
    return '';
  }
}

/**
 * Secure storage utility for encrypting sensitive data in localStorage
 * Provides encryption/decryption for tokens and other sensitive information
 */
// eslint-disable-next-line import/prefer-default-export
export const secureStorage = {
  /**
   * Store encrypted value in localStorage
   * @param {string} key - Storage key
   * @param {any} value - Value to encrypt and store
   */
  setItem(key, value) {
    try {
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(value), ENCRYPTION_KEY).toString();
      localStorage.setItem(key, encrypted);
    } catch (error) {
      // eslint-disable-next-line no-console -- surface encryption failures during development
      console.error('Encryption error:', error);
      throw new Error('Failed to store secure data');
    }
  },

  /**
   * Retrieve and decrypt value from localStorage
   * @param {string} key - Storage key
   * @returns {any|null} Decrypted value or null if not found
   */
  getItem(key) {
    try {
      const raw = localStorage.getItem(key);
      if (raw == null || raw === '') return null;

      // Plain JWT or legacy unencrypted value (must not run through AES.decrypt — it throws Malformed UTF-8)
      if (looksLikeJwt(raw) || !looksLikeCryptoJsCiphertext(raw)) {
        return parseStoredPlain(raw);
      }

      const decrypted = CryptoJS.AES.decrypt(raw, ENCRYPTION_KEY);
      const decryptedStr = safeUtf8FromDecrypted(decrypted);

      if (!decryptedStr) {
        if (looksLikeCryptoJsCiphertext(raw)) {
          try {
            localStorage.removeItem(key);
          } catch {
            /* ignore */
          }
          return null;
        }
        return parseStoredPlain(raw);
      }

      try {
        return JSON.parse(decryptedStr);
      } catch {
        return parseStoredPlain(raw);
      }
    } catch {
      const raw = localStorage.getItem(key);
      if (raw == null) return null;
      if (looksLikeJwt(raw)) {
        return parseStoredPlain(raw);
      }
      try {
        localStorage.removeItem(key);
      } catch {
        /* ignore */
      }
      return null;
    }
  },

  /**
   * Remove item from localStorage
   * @param {string} key - Storage key
   */
  removeItem(key) {
    localStorage.removeItem(key);
  },

  /**
   * Clear all items from localStorage
   */
  clear() {
    localStorage.clear();
  },
};
